import {
  Dialog,
  Button,
  Flex,
  Text,
  Link,
  Radio,
  Checkbox,
} from '@servicetitan/anvil2';
import { useDependencies } from '@servicetitan/react-ioc';
import { HELP_CENTER_BUTTON_STORE } from '@servicetitan/help-center';
import UserStatusModule from '@modules/UserStatusModule';
import BrowserModule from '@modules/BrowserModule';
import ToastModule from '@modules/ToastModule';
import appManager from '@app/appManager';
import { mainLayoutLocators } from '@locators/main-layout';
import { strings } from './strings';
import { useState } from 'react';

export interface ResetAccountModalProps {
  open: boolean;
  onClose: () => void;
}

export function ResetAccountModal({ open, onClose }: ResetAccountModalProps) {
  const [helpCenterButtonStore] = useDependencies(HELP_CENTER_BUTTON_STORE);
  const [resetAcknowledgement, setResetAcknowledgement] = useState(false);
  const [keepTasks, setKeepTasks] = useState(true);

  /**
   * Handles the account reset process when triggered by the user.
   *
   * This function performs a three-step recovery process designed to resolve
   * common technical issues without requiring the user to fully sign out:
   *
   * 1. Refresh User Status - Syncs the user's availability status with the backend
   * 2. Clear Browser Data - Removes cached data while preserving authentication
   * 3. Reload Page - Triggers a fresh application load
   *
   * Important characteristics:
   * - Fails fast: If any step fails, subsequent steps are skipped
   * - Non-destructive: Auth tokens and session data are preserved
   * - User feedback: Error toasts are shown for each failure point
   * - All-or-nothing: Only reloads if all steps succeed
   *
   * Error handling strategy:
   * - Each step has isolated try-catch blocks for granular error handling
   * - Errors are logged with context for debugging
   * - User-friendly error messages are shown via toast notifications
   */
  const handleResetAccount = async () => {
    // Close the modal first to provide immediate feedback
    onClose();

    // Step 1: Reset user status
    try {
      await UserStatusModule.refreshUserStatus();
    } catch (error) {
      appManager
        .logger('reset-account-modal')
        .error('Failed to refresh user status during reset', error);

      ToastModule.showToast({
        title: strings.errors.refreshUserStatusFailed,
        status: 'critical',
        duration: 5000,
      });
      // Don't continue if user status refresh fails
      return;
    }

    // Step 2: Clear browsing data
    try {
      await BrowserModule.clearBrowserData({ keepTasks });
    } catch (error) {
      appManager
        .logger('reset-account-modal')
        .error('Failed to clear browser data during reset', error);

      ToastModule.showToast({
        title: strings.errors.resetAccountFailed,
        status: 'critical',
        duration: 5000,
      });
      // Don't reload if we failed to clear data
      return;
    }

    // Step 3: Reload the page (only if both previous steps succeeded)
    window.location.reload();
  };

  const handleHelpCenterLinkClick = () => {
    // Close the modal first
    onClose();
    // Open the help center drawer
    helpCenterButtonStore.show();
  };

  const handleResetAcknowledgementChange = (
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setResetAcknowledgement(event?.target.checked ?? false);
  };

  const handleKeepTasksChange = (value: boolean) => {
    setKeepTasks(value);
  };

  return (
    <Dialog
      size='large'
      open={open}
      onClose={onClose}
      id={mainLayoutLocators.resetAccountModal.dialog}
    >
      <Dialog.Header>{strings.title}</Dialog.Header>
      <Dialog.Content>
        <Flex direction='column' gap='6'>
          <Text>{strings.body.havingTechnicalIssues}</Text>
          <Flex direction='column' gap='2' justifyContent='flex-end'>
            <Text el='h4' variant='headline' size='small' className='m-b-0'>
              {strings.headers.activeCalls}
            </Text>
            <Text>{strings.body.ifYouAreOnACall}</Text>
            <Checkbox
              label={strings.labels.checkbox}
              checked={resetAcknowledgement}
              onChange={handleResetAcknowledgementChange}
              id={mainLayoutLocators.resetAccountModal.acknowledgementCheckbox}
            />
          </Flex>
          <Flex direction='column' gap='2' justifyContent='flex-end'>
            <Text el='h4' variant='headline' size='small' className='m-b-0'>
              {strings.headers.inboxTasks}
            </Text>
            <Radio
              label={strings.labels.radioButtons.yes}
              checked={keepTasks}
              onChange={() => handleKeepTasksChange(true)}
              id={mainLayoutLocators.resetAccountModal.keepTasksRadio}
            />
            <Radio
              label={strings.labels.radioButtons.no}
              description={strings.labels.radioButtons.noDescription}
              checked={!keepTasks}
              onChange={() => handleKeepTasksChange(false)}
              id={mainLayoutLocators.resetAccountModal.clearTasksRadio}
            />
          </Flex>
          <Text>
            {strings.body.ifProblemContinues}
            <Link appearance='primary' onClick={handleHelpCenterLinkClick}>
              {strings.body.helpCenter}
            </Link>
          </Text>
        </Flex>
      </Dialog.Content>
      <Dialog.Footer>
        <Flex gap='3' justifyContent='flex-end'>
          <Dialog.CancelButton
            onClick={onClose}
            id={mainLayoutLocators.resetAccountModal.cancelButton}
          >
            {strings.footer.secondaryText}
          </Dialog.CancelButton>
          <Button
            appearance='primary'
            onClick={handleResetAccount}
            disabled={!resetAcknowledgement}
            id={mainLayoutLocators.resetAccountModal.resetButton}
          >
            {strings.footer.primaryText}
          </Button>
        </Flex>
      </Dialog.Footer>
    </Dialog>
  );
}

export default ResetAccountModal;
